import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, X, Trash2, Printer, ClipboardList, Loader2, Pencil, Check, Lock, FileSpreadsheet, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft } from 'lucide-react';
import { EmptyState } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===== مرجع البحث الموحَّد: تصدير كل الكشوف كملف Excel حقيقي (.xlsx) =====
async function handleExportAll(token, setExporting) {
  try {
    setExporting(true);
    const res = await fetch(`${API_URL}/api/invoice-matching/sheets/export-all.xlsx`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
      toast.error('فشل في تصدير الملف');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `كل-كشوف-الفواتير-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('تم تصدير الملف بنجاح');
  } catch (e) {
    toast.error('فشل في التصدير');
  } finally {
    setExporting(false);
  }
}

// ===================================================================
// 1) Sheets List Tab
// ===================================================================
export function SheetsListTab({ token, onPrint, onEdit, refreshKey, onRefresh }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState('');
  const [filterReceiptNumber, setFilterReceiptNumber] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterCreatedBy, setFilterCreatedBy] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const refresh = () => setReloadKey(k => k + 1);

  // استخراج القيم الفريدة للفلاتر
  const uniqueFilters = useMemo(() => {
    const suppliers = new Set();
    const projects = new Set();
    const creators = new Set();
    sheets.forEach(s => {
      if (s.created_by) creators.add(s.created_by);
      (s.items || []).forEach(it => {
        if (it.supplier_name) suppliers.add(it.supplier_name);
        if (it.project_name) projects.add(`${it.project_number || ''} - ${it.project_name}`.trim().replace(/^-\s*/, ''));
        else if (it.project_number) projects.add(String(it.project_number));
      });
    });
    return {
      suppliers: Array.from(suppliers).sort(),
      projects: Array.from(projects).sort(),
      creators: Array.from(creators).sort(),
    };
  }, [sheets]);

  // فلترة شاملة: البحث النصي + الفلاتر المتقدمة
  const normalize = (v) => String(v ?? '').trim().toLowerCase();
  const q = search.trim().toLowerCase().replace(/^#+/, '');
  const invQ = filterInvoiceNumber.trim().toLowerCase().replace(/^#+/, '');
  const recQ = filterReceiptNumber.trim().toLowerCase().replace(/^#+/, '');
  const filteredSheets = sheets.filter((s) => {
    // فلتر التاريخ (على sheet_date) — inclusive
    const sd = String(s.sheet_date || '').trim().slice(0, 10);
    if (filterDateFrom && (!sd || sd < filterDateFrom)) return false;
    if (filterDateTo && (!sd || sd > filterDateTo)) return false;
    // فلتر المُدخِل (created_by) — تطابق تام
    if (filterCreatedBy && String(s.created_by || '').trim() !== filterCreatedBy) return false;
    // فلاتر داخل البنود (مورد/مشروع/رقم فاتورة/رقم استلام)
    const items = Array.isArray(s.items) ? s.items : [];
    if (filterSupplier) {
      if (!items.some(it => String(it.supplier_name || '').trim() === filterSupplier)) return false;
    }
    if (filterProject) {
      if (!items.some(it => {
        const combo = `${it.project_number || ''} - ${it.project_name || ''}`.trim().replace(/^-\s*/, '');
        return combo === filterProject || String(it.project_number || '').trim() === filterProject;
      })) return false;
    }
    // بحث دقيق برقم الفاتورة (تطابق تام — case-insensitive بعد إزالة الفراغات)
    if (invQ) {
      if (!items.some(it => normalize(it.invoice_number) === invQ)) return false;
    }
    // بحث دقيق برقم الاستلام (تطابق تام — case-insensitive بعد إزالة الفراغات)
    if (recQ) {
      if (!items.some(it => normalize(it.receipt_number) === recQ)) return false;
    }
    // البحث النصي العام (إن وُجد)
    if (q) {
      const top = [
        s.sheet_number, s.sheet_date, s.created_by, s.notes,
        s.total_amount, s.invoices_count, s.suppliers_count,
      ].map(normalize).join(' ');
      if (top.includes(q)) return true;
      return items.some((it) => {
        const inner = [
          it.invoice_number, it.invoice_date, it.amount,
          it.receipt_number, it.receipt_date, it.delivery_number,
          it.supplier_name, it.project_number, it.project_name,
        ].map(normalize).join(' ');
        return inner.includes(q);
      });
    }
    return true;
  });

  const clearAllFilters = () => {
    setSearch(''); setFilterInvoiceNumber(''); setFilterReceiptNumber('');
    setFilterSupplier(''); setFilterProject(''); setFilterCreatedBy('');
    setFilterDateFrom(''); setFilterDateTo('');
  };
  const activeFiltersCount = [filterInvoiceNumber, filterReceiptNumber, filterSupplier, filterProject, filterCreatedBy, filterDateFrom, filterDateTo].filter(Boolean).length;

  // إعادة صفحة إلى 1 عند تغيير البحث أو حجم الصفحة أو أي فلتر
  useEffect(() => { setPage(1); }, [q, pageSize, invQ, recQ, filterSupplier, filterProject, filterCreatedBy, filterDateFrom, filterDateTo]);

  const totalPages = Math.max(1, Math.ceil(filteredSheets.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedSheets = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSheets.slice(start, start + pageSize);
  }, [filteredSheets, currentPage, pageSize]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/invoice-matching/sheets?limit=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setSheets(data.sheets || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, reloadKey, refreshKey]);

  const handleDelete = async (sheet) => {
    if (sheet.status === 'confirmed') {
      toast.error('هذا الكشف مؤكَّد ولا يمكن حذفه');
      return;
    }
    if (!window.confirm(`حذف كشف الفواتير #${sheet.sheet_number}؟ سيتم فك ربط الفواتير من الاستلامات.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/invoice-matching/sheets/${sheet.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('تم الحذف');
        refresh();
        onRefresh && onRefresh();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل في الحذف');
      }
    } catch {
      toast.error('فشل في الحذف');
    }
  };

  const handleConfirm = async (sheet) => {
    if (sheet.status === 'confirmed') return;
    if (!window.confirm(`تأكيد كشف الفواتير #${sheet.sheet_number} نهائياً؟\n\nبعد التأكيد لا يمكن تعديله أو حذفه.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/invoice-matching/sheets/${sheet.id}/confirm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`تم تأكيد الكشف #${sheet.sheet_number}`);
        refresh();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل في التأكيد');
      }
    } catch {
      toast.error('فشل في التأكيد');
    }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>;
  if (sheets.length === 0) return <EmptyState icon={ClipboardList} title="لا توجد كشوف فواتير محفوظة" subtitle="اضغط 'إنشاء كشف فواتير جديد' للبدء" />;

  return (
    <div className="space-y-3">
      {/* شريط أعلى: زر تصدير شامل لكل الكشوف */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-gray-600">
          إجمالي الكشوف المحفوظة: <span className="font-bold text-gray-900">{sheets.length}</span>
        </div>
        <button
          onClick={() => handleExportAll(token, setExporting)}
          disabled={exporting}
          data-testid="export-all-sheets-btn"
          className="h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg text-sm font-bold shadow flex items-center gap-2"
          title="تصدير كل الكشوف وبنودها (مرجع موحَّد للبحث عن أي استلام أو فاتورة)"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          {exporting ? 'جارٍ التصدير...' : 'تصدير شامل لأكسل'}
        </button>
      </div>
      {/* Search bar - يبحث في كل الحقول داخل وخارج البنود */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ابحث برقم الكشف، رقم الاستلام، رقم الفاتورة، المورد، المشروع، المبلغ، المنشئ، الملاحظات، التاريخ..."
          data-testid="sheets-search-input"
          className="w-full h-11 border-2 border-gray-200 rounded-xl pr-10 pl-10 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none bg-white"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            data-testid="sheets-search-clear"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400"
            title="مسح"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Advanced Search Modal Trigger */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setShowAdvanced(true)}
          data-testid="open-invoice-search-modal"
          className={`text-xs font-semibold px-3 py-1.5 rounded-lg border-2 transition ${activeFiltersCount > 0 ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 border-purple-200 hover:bg-purple-50'}`}
        >
          🔍 تقرير البحث عن الفواتير {activeFiltersCount > 0 && <span className="ml-1 bg-white text-purple-700 rounded-full px-1.5 text-[10px] font-bold">{activeFiltersCount}</span>}
        </button>
        {(activeFiltersCount > 0 || search) && (
          <button
            onClick={clearAllFilters}
            data-testid="clear-all-filters"
            className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
          >
            مسح كل الفلاتر
          </button>
        )}
      </div>

      {showAdvanced && (
        <InvoiceSearchModal
          onClose={() => setShowAdvanced(false)}
          sheets={sheets}
          uniqueFilters={uniqueFilters}
          filterInvoiceNumber={filterInvoiceNumber}
          setFilterInvoiceNumber={setFilterInvoiceNumber}
          filterReceiptNumber={filterReceiptNumber}
          setFilterReceiptNumber={setFilterReceiptNumber}
          filterSupplier={filterSupplier}
          setFilterSupplier={setFilterSupplier}
          filterProject={filterProject}
          setFilterProject={setFilterProject}
          filterCreatedBy={filterCreatedBy}
          setFilterCreatedBy={setFilterCreatedBy}
          filterDateFrom={filterDateFrom}
          setFilterDateFrom={setFilterDateFrom}
          filterDateTo={filterDateTo}
          setFilterDateTo={setFilterDateTo}
          onOpenSheet={(sheet) => { if (typeof onEdit === 'function') onEdit(sheet); }}
          onPrintSheet={(sheet) => { if (typeof onPrint === 'function') onPrint(sheet); }}
        />
      )}

      {/* Results count when filtering */}
      {(q || activeFiltersCount > 0) && (
        <div className="text-xs text-gray-600 px-1" data-testid="sheets-search-count">
          عدد النتائج: <span className="font-bold text-purple-700">{filteredSheets.length}</span> من {sheets.length}
        </div>
      )}

      {filteredSheets.length === 0 ? (
        <EmptyState icon={Search} title="لا توجد نتائج" subtitle="جرب كلمة بحث مختلفة" />
      ) : (
      <>
      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {paginatedSheets.map((sheet) => {
          const isConfirmed = sheet.status === 'confirmed';
          return (
          <div key={sheet.id} className={`border-2 rounded-2xl p-3 bg-white shadow-sm ${isConfirmed ? 'border-emerald-300' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xl font-bold text-purple-700">كشف #{sheet.sheet_number}</span>
              <div className="flex items-center gap-2">
                {isConfirmed && (
                  <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Lock className="w-3 h-3" /> مؤكَّد
                  </span>
                )}
                <span className="text-xs text-gray-500">{sheet.sheet_date}</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm mb-3 text-center">
              <div className="bg-purple-50 rounded-lg py-1.5">
                <div className="font-bold text-purple-700">{sheet.invoices_count}</div>
                <div className="text-[10px] text-purple-600">فاتورة</div>
              </div>
              <div className="bg-emerald-50 rounded-lg py-1.5">
                <div className="font-bold text-emerald-700">{sheet.suppliers_count}</div>
                <div className="text-[10px] text-emerald-600">مورد</div>
              </div>
              <div className="bg-amber-50 rounded-lg py-1.5">
                <div className="font-bold text-amber-700 text-xs">{Number(sheet.total_amount || 0).toLocaleString('en-US')}</div>
                <div className="text-[10px] text-amber-600">إجمالي</div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-3">بواسطة: {sheet.created_by}{isConfirmed && sheet.confirmed_by ? ` • تأكيد: ${sheet.confirmed_by}` : ''}</div>
            <div className="flex gap-2">
              <button
                onClick={() => onPrint(sheet)}
                className="flex-1 h-10 bg-blue-600 active:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5"
                data-testid={`mobile-print-${sheet.sheet_number}`}
              >
                <Printer className="w-4 h-4" /> طباعة
              </button>
              {!isConfirmed && (
                <>
                  <button
                    onClick={() => onEdit(sheet)}
                    className="h-10 px-3 bg-orange-50 active:bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center"
                    title="تعديل"
                    data-testid={`mobile-edit-${sheet.sheet_number}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleConfirm(sheet)}
                    className="h-10 px-3 bg-emerald-50 active:bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center"
                    title="تأكيد"
                    data-testid={`mobile-confirm-${sheet.sheet_number}`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(sheet)}
                    className="h-10 px-3 bg-red-50 active:bg-red-100 text-red-600 rounded-lg flex items-center justify-center"
                    title="حذف"
                    data-testid={`mobile-delete-${sheet.sheet_number}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-purple-50 text-purple-900">
            <tr>
              <th className="px-3 py-2 text-right">رقم الكشف</th>
              <th className="px-3 py-2 text-right">التاريخ</th>
              <th className="px-3 py-2 text-right">عدد الفواتير</th>
              <th className="px-3 py-2 text-right">عدد الموردين</th>
              <th className="px-3 py-2 text-right">الإجمالي</th>
              <th className="px-3 py-2 text-right">الحالة</th>
              <th className="px-3 py-2 text-right">بواسطة</th>
              <th className="px-3 py-2 text-right">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSheets.map((sheet) => {
              const isConfirmed = sheet.status === 'confirmed';
              return (
              <tr key={sheet.id} className={`border-t hover:bg-gray-50 ${isConfirmed ? 'bg-emerald-50/30' : ''}`} data-testid={`sheet-row-${sheet.sheet_number}`}>
                <td className="px-3 py-2 font-bold text-purple-700">#{sheet.sheet_number}</td>
                <td className="px-3 py-2">{sheet.sheet_date}</td>
                <td className="px-3 py-2">{sheet.invoices_count}</td>
                <td className="px-3 py-2">{sheet.suppliers_count}</td>
                <td className="px-3 py-2 font-semibold text-emerald-700">{Number(sheet.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2">
                  {isConfirmed ? (
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> مؤكَّد
                    </span>
                  ) : (
                    <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">مسودة</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-600">{sheet.created_by}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button
                      onClick={() => onPrint(sheet)}
                      className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                      title="طباعة PDF"
                      data-testid={`print-${sheet.sheet_number}`}
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                    {!isConfirmed && (
                      <>
                        <button
                          onClick={() => onEdit(sheet)}
                          className="p-1.5 hover:bg-orange-50 rounded text-orange-600"
                          title="تعديل"
                          data-testid={`edit-${sheet.sheet_number}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleConfirm(sheet)}
                          className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600"
                          title="تأكيد الكشف"
                          data-testid={`confirm-${sheet.sheet_number}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sheet)}
                          className="p-1.5 hover:bg-red-50 rounded text-red-600"
                          title="حذف"
                          data-testid={`delete-${sheet.sheet_number}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Bar */}
      {filteredSheets.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t bg-white rounded-lg px-3 py-2" data-testid="pagination-bar">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              عرض <span className="font-bold text-gray-900">{(currentPage - 1) * pageSize + 1}</span>
              {' - '}
              <span className="font-bold text-gray-900">{Math.min(currentPage * pageSize, filteredSheets.length)}</span>
              {' من '}
              <span className="font-bold text-purple-700">{filteredSheets.length}</span>
              {q ? ' نتيجة بحث' : ' كشف'}
            </span>
            <div className="flex items-center gap-1.5 mr-4">
              <label className="text-xs text-gray-500">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                data-testid="page-size-select"
                className="h-8 px-2 border rounded-md text-sm bg-white focus:border-purple-500 outline-none"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={currentPage <= 1}
              data-testid="page-first-btn"
              className="h-8 w-8 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="الصفحة الأولى"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              data-testid="page-prev-btn"
              className="h-8 w-8 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="السابق"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="px-3 h-8 flex items-center gap-1.5 text-sm">
              <span>صفحة</span>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v)) setPage(Math.min(Math.max(1, v), totalPages));
                }}
                min={1}
                max={totalPages}
                data-testid="page-number-input"
                className="w-12 h-7 border rounded text-center text-sm focus:border-purple-500 outline-none"
              />
              <span className="text-gray-500">من {totalPages}</span>
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              data-testid="page-next-btn"
              className="h-8 w-8 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="التالي"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={currentPage >= totalPages}
              data-testid="page-last-btn"
              className="h-8 w-8 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="الصفحة الأخيرة"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ===================================================================
// 2) Invoice Search Modal — بحث شامل عبر كل الكشوف بعرض جدولي
// ===================================================================
function InvoiceSearchModal({
  onClose, sheets, uniqueFilters,
  filterInvoiceNumber, setFilterInvoiceNumber,
  filterReceiptNumber, setFilterReceiptNumber,
  filterSupplier, setFilterSupplier,
  filterProject, setFilterProject,
  filterCreatedBy, setFilterCreatedBy,
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  onOpenSheet, onPrintSheet,
}) {
  const [localInv, setLocalInv] = useState(filterInvoiceNumber || '');
  const [localRec, setLocalRec] = useState(filterReceiptNumber || '');
  const [localSup, setLocalSup] = useState(filterSupplier || '');
  const [localProj, setLocalProj] = useState(filterProject || '');
  const [localCB, setLocalCB] = useState(filterCreatedBy || '');
  const [localFrom, setLocalFrom] = useState(filterDateFrom || '');
  const [localTo, setLocalTo] = useState(filterDateTo || '');
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(50);

  // نبني قائمة مسطّحة من البنود (سطر لكل فاتورة داخل كشف)
  const rows = useMemo(() => {
    const norm = (v) => String(v ?? '').trim().toLowerCase();
    const invQ = String(localInv || '').trim().toLowerCase().replace(/^#+/, '');
    const recQ = String(localRec || '').trim().toLowerCase().replace(/^#+/, '');
    const out = [];
    (sheets || []).forEach((s) => {
      const items = Array.isArray(s.items) ? s.items : [];
      // فلتر التاريخ على تاريخ الكشف
      const sd = String(s.sheet_date || '').trim().slice(0, 10);
      if (localFrom && (!sd || sd < localFrom)) return;
      if (localTo && (!sd || sd > localTo)) return;
      if (localCB && String(s.created_by || '').trim() !== localCB) return;
      items.forEach((it, idx) => {
        if (localSup && String(it.supplier_name || '').trim() !== localSup) return;
        if (localProj) {
          const combo = `${it.project_number || ''} - ${it.project_name || ''}`.trim().replace(/^-\s*/, '');
          if (combo !== localProj && String(it.project_number || '').trim() !== localProj) return;
        }
        if (invQ && norm(it.invoice_number) !== invQ) return;
        if (recQ && norm(it.receipt_number) !== recQ) return;
        out.push({
          key: `${s.id}-${idx}`,
          sheet: s,
          item: it,
        });
      });
    });
    return out;
  }, [sheets, localInv, localRec, localSup, localProj, localCB, localFrom, localTo]);

  // إعادة الصفحة إلى 1 عند تغيير أي فلتر
  useEffect(() => { setModalPage(1); }, [localInv, localRec, localSup, localProj, localCB, localFrom, localTo, modalPageSize]);

  const totalPagesModal = Math.max(1, Math.ceil(rows.length / modalPageSize));
  const currentPageModal = Math.min(modalPage, totalPagesModal);
  const pagedRows = useMemo(() => {
    const start = (currentPageModal - 1) * modalPageSize;
    return rows.slice(start, start + modalPageSize);
  }, [rows, currentPageModal, modalPageSize]);

  const applyAndClose = () => {
    setFilterInvoiceNumber(localInv);
    setFilterReceiptNumber(localRec);
    setFilterSupplier(localSup);
    setFilterProject(localProj);
    setFilterCreatedBy(localCB);
    setFilterDateFrom(localFrom);
    setFilterDateTo(localTo);
    onClose && onClose();
  };
  const clearLocal = () => {
    setLocalInv(''); setLocalRec(''); setLocalSup(''); setLocalProj('');
    setLocalCB(''); setLocalFrom(''); setLocalTo('');
  };

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.item.amount || 0), 0);

  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-2 sm:p-4"
      data-testid="invoice-search-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden"
        data-testid="invoice-search-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b bg-gradient-to-l from-purple-50 to-white">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-purple-800">تقرير البحث عن الفواتير</h2>
            <span className="text-xs bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-semibold" data-testid="invoice-search-count">
              {rows.length} نتيجة
            </span>
          </div>
          <button
            onClick={onClose}
            data-testid="invoice-search-close-btn"
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
            title="إغلاق"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters bar */}
        <div className="px-4 py-3 border-b bg-gray-50 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">رقم الفاتورة (تطابق تام)</label>
            <input
              type="text"
              value={localInv}
              onChange={(e) => setLocalInv(e.target.value)}
              placeholder="مثال: 12345"
              data-testid="search-modal-invoice"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm focus:border-purple-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">رقم الاستلام (تطابق تام)</label>
            <input
              type="text"
              value={localRec}
              onChange={(e) => setLocalRec(e.target.value)}
              placeholder="مثال: 456"
              data-testid="search-modal-receipt"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm focus:border-purple-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">المورد</label>
            <select
              value={localSup}
              onChange={(e) => setLocalSup(e.target.value)}
              data-testid="search-modal-supplier"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm bg-white focus:border-purple-500 outline-none"
            >
              <option value="">— كل الموردين —</option>
              {(uniqueFilters?.suppliers || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">المشروع</label>
            <select
              value={localProj}
              onChange={(e) => setLocalProj(e.target.value)}
              data-testid="search-modal-project"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm bg-white focus:border-purple-500 outline-none"
            >
              <option value="">— كل المشاريع —</option>
              {(uniqueFilters?.projects || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">المُدخِل</label>
            <select
              value={localCB}
              onChange={(e) => setLocalCB(e.target.value)}
              data-testid="search-modal-created-by"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm bg-white focus:border-purple-500 outline-none"
            >
              <option value="">— الكل —</option>
              {(uniqueFilters?.creators || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">من تاريخ الكشف</label>
            <input
              type="date"
              value={localFrom}
              onChange={(e) => setLocalFrom(e.target.value)}
              data-testid="search-modal-date-from"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm focus:border-purple-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">إلى تاريخ الكشف</label>
            <input
              type="date"
              value={localTo}
              onChange={(e) => setLocalTo(e.target.value)}
              data-testid="search-modal-date-to"
              className="w-full h-9 border-2 border-gray-200 rounded-lg px-2 text-sm focus:border-purple-500 outline-none"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={clearLocal}
              data-testid="search-modal-clear-btn"
              className="h-9 px-3 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold flex-1"
            >
              مسح الفلاتر
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-3">
          {rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <div className="text-sm">لا توجد نتائج مطابقة. حاول تعديل الفلاتر.</div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="invoice-search-results-table">
              <thead className="bg-purple-50 text-purple-900 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-right">#</th>
                  <th className="px-2 py-2 text-right">رقم الفاتورة</th>
                  <th className="px-2 py-2 text-right">تاريخ الفاتورة</th>
                  <th className="px-2 py-2 text-right">المورد</th>
                  <th className="px-2 py-2 text-right">المشروع</th>
                  <th className="px-2 py-2 text-right">المبلغ</th>
                  <th className="px-2 py-2 text-right">رقم الاستلام</th>
                  <th className="px-2 py-2 text-right">تاريخ الاستلام</th>
                  <th className="px-2 py-2 text-right">الكشف</th>
                  <th className="px-2 py-2 text-right">تاريخ الكشف</th>
                  <th className="px-2 py-2 text-right">الحالة</th>
                  <th className="px-2 py-2 text-right">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r, i) => {
                  const s = r.sheet;
                  const it = r.item;
                  const isConfirmed = s.status === 'confirmed';
                  const projectLabel = `${it.project_number || ''} - ${it.project_name || ''}`.trim().replace(/^-\s*/, '') || '—';
                  const globalIdx = (currentPageModal - 1) * modalPageSize + i;
                  return (
                    <tr key={r.key} className="border-t hover:bg-purple-50/40" data-testid={`invoice-search-row-${globalIdx}`}>
                      <td className="px-2 py-2 text-gray-500">{globalIdx + 1}</td>
                      <td className="px-2 py-2 font-semibold text-gray-900">{it.invoice_number || '—'}</td>
                      <td className="px-2 py-2 text-gray-600">{it.invoice_date || '—'}</td>
                      <td className="px-2 py-2 text-gray-800">{it.supplier_name || '—'}</td>
                      <td className="px-2 py-2 text-gray-800">{projectLabel}</td>
                      <td className="px-2 py-2 font-semibold text-emerald-700 whitespace-nowrap">
                        {Number(it.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-2 py-2 text-gray-800">{it.receipt_number || '—'}</td>
                      <td className="px-2 py-2 text-gray-600">{it.receipt_date || '—'}</td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => onOpenSheet && onOpenSheet(s)}
                          data-testid={`open-sheet-link-${globalIdx}`}
                          className="text-purple-700 font-bold hover:underline"
                          title={isConfirmed ? 'كشف مؤكَّد — عرض/طباعة' : 'فتح الكشف للتعديل'}
                        >
                          #{s.sheet_number}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-gray-600">{s.sheet_date || '—'}</td>
                      <td className="px-2 py-2">
                        {isConfirmed ? (
                          <span className="bg-emerald-100 text-emerald-700 text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                            <Lock className="w-3 h-3" /> مؤكَّد
                          </span>
                        ) : (
                          <span className="bg-amber-100 text-amber-700 text-[11px] font-medium px-2 py-0.5 rounded-full">مسودة</span>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => onPrintSheet && onPrintSheet(s)}
                            data-testid={`search-print-${globalIdx}`}
                            className="p-1.5 hover:bg-blue-50 rounded text-blue-600"
                            title="طباعة الكشف"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          {!isConfirmed && (
                            <button
                              onClick={() => onOpenSheet && onOpenSheet(s)}
                              data-testid={`search-edit-${globalIdx}`}
                              className="p-1.5 hover:bg-orange-50 rounded text-orange-600"
                              title="تعديل الكشف"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-purple-200">
                <tr>
                  <td colSpan={5} className="px-2 py-2 text-right font-bold text-gray-700">الإجمالي</td>
                  <td className="px-2 py-2 font-bold text-emerald-700" data-testid="invoice-search-total">
                    {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td colSpan={6}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Pagination bar (inside modal) */}
        {rows.length > 0 && (
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-t bg-white text-sm" data-testid="modal-pagination-bar">
            <div className="flex items-center gap-2 text-gray-600 text-xs">
              <span>
                عرض <span className="font-bold text-gray-900">{(currentPageModal - 1) * modalPageSize + 1}</span>
                {' - '}
                <span className="font-bold text-gray-900">{Math.min(currentPageModal * modalPageSize, rows.length)}</span>
                {' من '}
                <span className="font-bold text-purple-700">{rows.length}</span>
              </span>
              <label className="mr-4">عرض:</label>
              <select
                value={modalPageSize}
                onChange={(e) => setModalPageSize(Number(e.target.value))}
                data-testid="modal-page-size"
                className="h-7 px-2 border rounded-md text-xs bg-white focus:border-purple-500 outline-none"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setModalPage(1)}
                disabled={currentPageModal <= 1}
                data-testid="modal-page-first"
                className="h-7 w-7 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setModalPage(p => Math.max(1, p - 1))}
                disabled={currentPageModal <= 1}
                data-testid="modal-page-prev"
                className="h-7 w-7 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <span className="px-2 text-xs">صفحة <span className="font-bold text-purple-700">{currentPageModal}</span> من {totalPagesModal}</span>
              <button
                onClick={() => setModalPage(p => Math.min(totalPagesModal, p + 1))}
                disabled={currentPageModal >= totalPagesModal}
                data-testid="modal-page-next"
                className="h-7 w-7 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setModalPage(totalPagesModal)}
                disabled={currentPageModal >= totalPagesModal}
                data-testid="modal-page-last"
                className="h-7 w-7 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t bg-gray-50">
          <div className="text-xs text-gray-600">
            {rows.length > 0 && (
              <>عدد النتائج: <span className="font-bold text-purple-700">{rows.length}</span> • الإجمالي: <span className="font-bold text-emerald-700">{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              data-testid="search-modal-cancel-btn"
              className="h-9 px-4 rounded-lg border-2 border-gray-200 text-gray-700 hover:bg-gray-100 text-sm font-semibold"
            >
              إغلاق
            </button>
            <button
              onClick={applyAndClose}
              data-testid="search-modal-apply-btn"
              className="h-9 px-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold shadow"
            >
              تطبيق الفلاتر على القائمة
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

